import React, { useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ================================================================== */
/*  Design tokens                                                      */
/* ================================================================== */
const BLUE = '#3B82F6';
const TRACK = '#EEF2FF';

const PALETTE = [
  '#2563EB', '#0EA5E9', '#8B5CF6', '#10B981',
  '#F59E0B', '#F43F5E', '#06B6D4', '#6366F1',
];

const STATUS_COLOR = {
  'under review': '#3B82F6',
  review:         '#3B82F6',
  applied:        '#93C5FD',
  pending:        '#93C5FD',
  interview:      '#22C55E',
  'interview scheduled': '#22C55E',
  shortlisted:    '#6366F1',
  accepted:       '#16A34A',
  hired:          '#16A34A',
  rejected:       '#F87171',
};

/* ================================================================== */
/*  Shared tooltip                                                     */
/* ================================================================== */
const Tooltip = ({ x, y, children, visible }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        key="tt"
        initial={{ opacity: 0, scale: 0.92, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.13 }}
        style={{
          position: 'fixed',
          left: x,
          top: y,
          transform: 'translate(-50%, -110%)',
          background: '#1E293B',
          color: '#F1F5F9',
          fontSize: 11,
          fontWeight: 500,
          padding: '5px 9px',
          borderRadius: 6,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}
      >
        {children}
        <div style={{
          position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)',
          width: 0, height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '5px solid #1E293B',
        }} />
      </motion.div>
    )}
  </AnimatePresence>
);

/* ================================================================== */
/*  Empty hint                                                         */
/* ================================================================== */
const EmptyHint = ({ height = 120, label = 'No data yet' }) => (
  <div style={{ height, display: 'grid', placeItems: 'center' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: '#F1F5F9', display: 'grid', placeItems: 'center',
        margin: '0 auto 6px', color: '#94A3B8',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3" />
        </svg>
      </div>
      <p style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500, margin: 0 }}>{label}</p>
    </div>
  </div>
);

/* ================================================================== */
/*  Card shell                                                         */
/* ================================================================== */
export const ChartCard = ({ title, subtitle, children, right, className = '', compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    style={{
      background: '#fff',
      borderRadius: 12,
      border: '1px solid #E8ECF4',
      padding: compact ? '14px 16px' : '16px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#1E293B', lineHeight: 1.3 }}>{title}</h3>
        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 10.5, color: '#94A3B8', lineHeight: 1.4 }}>{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </motion.div>
);

/* ================================================================== */
/*  Donut chart                                                        */
/* ================================================================== */
export const Donut = ({
  data = [], size = 140, thickness = 18,
  centerTop, centerSub, showLegend = true,
}) => {
  const [tip, setTip] = useState(null);
  const clean = data.filter(d => d.value > 0);
  const total = clean.reduce((s, d) => s + d.value, 0);
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;

  if (!total) return <EmptyHint height={size} />;

  let acc = 0;
  const segments = clean.map((d, i) => {
    const len = (d.value / total) * C;
    const gap = clean.length > 1 ? 2 : 0;
    const seg = {
      ...d,
      len: Math.max(len - gap, 0.5),
      offset: -acc,
      color: d.color || PALETTE[i % PALETTE.length],
    };
    acc += len;
    return seg;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={TRACK} strokeWidth={thickness} />
          {segments.map((s, i) => (
            <motion.circle
              key={i}
              cx={size/2} cy={size/2} r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${s.len} ${C - s.len}`}
              strokeDashoffset={s.offset}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.06 * i }}
              style={{ cursor: 'pointer', transition: 'stroke-width 0.15s' }}
              onMouseEnter={e => {
                e.target.setAttribute('stroke-width', thickness + 4);
                setTip({ x: e.clientX, y: e.clientY, label: s.label, value: s.value, pct: Math.round((s.value/total)*100) });
              }}
              onMouseLeave={e => {
                e.target.setAttribute('stroke-width', thickness);
                setTip(null);
              }}
              onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
            />
          ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1 }}>
              {centerTop ?? total}
            </div>
            {centerSub && (
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94A3B8', marginTop: 2 }}>
                {centerSub}
              </div>
            )}
          </div>
        </div>
      </div>

      {tip && (
        <Tooltip x={tip.x} y={tip.y} visible>
          {tip.label} — {tip.value} ({tip.pct}%)
        </Tooltip>
      )}

      {showLegend && (
        <ul style={{ width: '100%', listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {segments.map((s, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span style={{ flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
              <span style={{ fontWeight: 700, color: '#1E293B', marginLeft: 4 }}>{s.value}</span>
              <span style={{ width: 30, textAlign: 'right', color: '#94A3B8', fontSize: 10 }}>
                {Math.round((s.value / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ================================================================== */
/*  Vertical bars                                                      */
/* ================================================================== */
export const VBars = ({ data = [], color = BLUE, barAreaPx = 110, suffix = '' }) => {
  const [tip, setTip] = useState(null);
  const max = Math.max(...data.map(d => d.value), 1);
  const hasData = data.some(d => d.value > 0);
  if (!hasData) return <EmptyHint height={barAreaPx + 24} />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6, height: barAreaPx }}>
        {data.map((d, i) => {
          const px = Math.round((d.value / max) * (barAreaPx - 20));
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#334155' }}>{d.value}{suffix}</span>
              <motion.div
                style={{ width: '100%', maxWidth: 36, borderRadius: '4px 4px 0 0', background: d.color || color, cursor: 'pointer', transition: 'filter 0.15s, transform 0.15s' }}
                initial={{ height: 0 }}
                whileInView={{ height: d.value > 0 ? Math.max(px, 4) : 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.05 * i }}
                onMouseEnter={e => {
                  e.currentTarget.style.filter = 'brightness(1.15)';
                  e.currentTarget.style.transform = 'scaleY(1.04)';
                  e.currentTarget.style.transformOrigin = 'bottom';
                  setTip({ x: e.clientX, y: e.clientY, label: d.label, value: d.value, suffix });
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.filter = '';
                  e.currentTarget.style.transform = '';
                  setTip(null);
                }}
                onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
              />
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', gap: 4, borderTop: '1px solid #F1F5F9', paddingTop: 6 }}>
        {data.map((d, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, fontWeight: 500, color: '#94A3B8', lineHeight: 1.3 }}>
            {d.label}
          </span>
        ))}
      </div>
      {tip && (
        <Tooltip x={tip.x} y={tip.y} visible>
          {tip.label}: {tip.value}{tip.suffix}
        </Tooltip>
      )}
    </div>
  );
};

/* ================================================================== */
/*  Area line chart                                                    */
/* ================================================================== */
export const AreaLine = ({ data = [], color = BLUE, height = 150 }) => {
  const [tip, setTip] = useState(null);
  const svgRef = useRef(null);
  const W = 600, H = 180;
  const pad = { l: 10, r: 10, t: 14, b: 24 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const n = data.length;
  const max = Math.max(...data.map(d => d.value), 1);
  const hasData = data.some(d => d.value > 0);
  const gid = useMemo(() => `area-${Math.random().toString(36).slice(2,8)}`, []);

  if (n === 0 || !hasData) return <EmptyHint height={height} label="No trend data yet" />;

  const x = i => pad.l + (n === 1 ? iw/2 : (i/(n-1)) * iw);
  const y = v => pad.t + ih - (v/max) * ih;
  const pts = data.map((d, i) => [x(i), y(d.value)]);
  const line = pts.map((p, i) => `${i?'L':'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${x(n-1).toFixed(1)},${(pad.t+ih).toFixed(1)} L${x(0).toFixed(1)},${(pad.t+ih).toFixed(1)} Z`;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height }}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25,0.5,0.75,1].map((g,i) => (
          <line key={i} x1={pad.l} x2={W-pad.r} y1={pad.t+ih*g} y2={pad.t+ih*g} stroke="#F1F5F9" strokeWidth="1" />
        ))}
        <motion.path d={area} fill={`url(#${gid})`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
        <motion.path
          d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeInOut' }}
        />
        {pts.map((p, i) => (
          <motion.circle
            key={i} cx={p[0]} cy={p[1]} r="4" fill="#fff" stroke={color} strokeWidth="2"
            initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.5 + i*0.07 }}
            style={{ cursor: 'pointer' }}
            onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, label: data[i].label, value: data[i].value })}
            onMouseLeave={() => setTip(null)}
            onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
          />
        ))}
      </svg>
      <div style={{ marginTop: 2, display: 'flex', justifyContent: 'space-between', padding: '0 4px' }}>
        {data.map((d,i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, fontWeight: 500, color: '#94A3B8' }}>{d.label}</span>
        ))}
      </div>
      {tip && <Tooltip x={tip.x} y={tip.y} visible>{tip.label}: {tip.value}</Tooltip>}
    </div>
  );
};

/* ================================================================== */
/*  Pie chart                                                          */
/* ================================================================== */
export const PieChart = ({ data = [], size = 140, showLegend = true }) => {
  const [tip, setTip] = useState(null);
  const clean = data.filter(d => d.value > 0);
  const total = clean.reduce((s, d) => s + d.value, 0);
  if (!total) return <EmptyHint height={size} />;

  const cx = size/2, cy = size/2, r = size/2 - 2;
  let a0 = -Math.PI/2;

  const slices = clean.map((d, i) => {
    const frac = d.value / total;
    const a1 = a0 + frac * 2 * Math.PI;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const path = clean.length === 1
      ? `M ${cx-r} ${cy} A ${r} ${r} 0 1 1 ${cx+r} ${cy} A ${r} ${r} 0 1 1 ${cx-r} ${cy} Z`
      : `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
    const slice = { ...d, path, color: d.color || PALETTE[i % PALETTE.length], pct: Math.round(frac * 100) };
    a0 = a1;
    return slice;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        {slices.map((s, i) => (
          <motion.path
            key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="2"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.07*i }}
            style={{ cursor: 'pointer', transition: 'filter 0.15s' }}
            onMouseEnter={e => {
              e.target.style.filter = 'brightness(1.15) drop-shadow(0 2px 4px rgba(0,0,0,0.15))';
              setTip({ x: e.clientX, y: e.clientY, label: s.label, value: s.value, pct: s.pct });
            }}
            onMouseLeave={e => { e.target.style.filter = ''; setTip(null); }}
            onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
          />
        ))}
      </svg>
      {tip && <Tooltip x={tip.x} y={tip.y} visible>{tip.label} — {tip.value} ({tip.pct}%)</Tooltip>}
      {showLegend && (
        <ul style={{ width: '100%', listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {slices.map((s, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span style={{ flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
              <span style={{ fontWeight: 700, color: '#1E293B' }}>{s.value}</span>
              <span style={{ width: 28, textAlign: 'right', color: '#94A3B8', fontSize: 10 }}>{s.pct}%</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ================================================================== */
/*  Horizontal bars                                                    */
/* ================================================================== */
export const HBars = ({ data = [], color = BLUE }) => {
  const [tip, setTip] = useState(null);
  const max = Math.max(...data.map(d => d.value), 1);
  if (!data.some(d => d.value > 0)) return <EmptyHint height={120} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
            <span style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{d.label}</span>
            <span style={{ fontWeight: 700, color: '#1E293B', marginLeft: 8 }}>{d.value}</span>
          </div>
          <div style={{ height: 8, width: '100%', borderRadius: 99, background: '#F1F5F9', overflow: 'hidden', cursor: 'pointer' }}
            onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, label: d.label, value: d.value })}
            onMouseLeave={() => setTip(null)}
            onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
          >
            <motion.div
              style={{ height: '100%', borderRadius: 99, background: d.color || color }}
              initial={{ width: 0 }}
              whileInView={{ width: `${(d.value/max)*100}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22,1,0.36,1], delay: 0.05*i }}
            />
          </div>
        </div>
      ))}
      {tip && <Tooltip x={tip.x} y={tip.y} visible>{tip.label}: {tip.value}</Tooltip>}
    </div>
  );
};

/* ================================================================== */
/*  Stacked horizontal bars                                            */
/* ================================================================== */
export const StackedBar = ({ rows = [], keys = [] }) => {
  const [tip, setTip] = useState(null);
  const totals = rows.map(r => r.segments.reduce((s, seg) => s + seg.value, 0));
  const max = Math.max(...totals, 1);
  if (!rows.length || max === 0 || totals.every(t => t === 0)) return <EmptyHint height={130} />;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => {
          const total = r.segments.reduce((s, seg) => s + seg.value, 0);
          return (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                <span style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.label}</span>
                <span style={{ fontWeight: 700, color: '#1E293B', marginLeft: 8 }}>{total}</span>
              </div>
              <motion.div
                style={{ height: 10, width: `${(total/max)*100}%`, borderRadius: 99, overflow: 'hidden', display: 'flex', cursor: 'pointer' }}
                initial={{ width: 0 }}
                whileInView={{ width: `${(total/max)*100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: [0.22,1,0.36,1], delay: 0.05*i }}
              >
                {r.segments.map((seg, j) =>
                  seg.value > 0 ? (
                    <span
                      key={j}
                      style={{ background: seg.color, width: `${total ? (seg.value/total)*100 : 0}%`, transition: 'filter 0.15s', cursor: 'pointer' }}
                      onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, label: `${r.label} · ${seg.key || keys[j]?.label || ''}`, value: seg.value })}
                      onMouseLeave={() => setTip(null)}
                      onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
                    />
                  ) : null
                )}
              </motion.div>
            </div>
          );
        })}
      </div>
      {keys.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: '6px 14px', borderTop: '1px solid #F1F5F9', paddingTop: 10 }}>
          {keys.map(k => (
            <span key={k.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: '#64748B' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: k.color }} />
              {k.label}
            </span>
          ))}
        </div>
      )}
      {tip && <Tooltip x={tip.x} y={tip.y} visible>{tip.label}: {tip.value}</Tooltip>}
    </div>
  );
};

/* ================================================================== */
/*  Bubble cluster                                                     */
/* ================================================================== */
export const Bubbles = ({ data = [], height = 180 }) => {
  const [tip, setTip] = useState(null);
  const clean = data.filter(d => d.value > 0).slice(0, 8);
  if (!clean.length) return <EmptyHint height={height} />;

  const W = 500, H = 180;
  const max = Math.max(...clean.map(d => d.value));
  const k = 40 / Math.sqrt(max);
  let px = 14;

  const placed = clean.map((d, i) => {
    const r = Math.max(Math.sqrt(d.value) * k, 14);
    px += r;
    const cx = px;
    let cy = H/2 + (i%2===0 ? -1:1) * (r*0.28) + (i%3===0 ? 5:-5);
    cy = Math.min(Math.max(cy, r+14), H-r-14);
    px += r + 8;
    return { ...d, r, cx, cy, color: d.color || PALETTE[i % PALETTE.length] };
  });

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} preserveAspectRatio="xMidYMid meet">
        {placed.map((b, i) => (
          <g key={i} style={{ cursor: 'pointer' }}>
            <motion.circle
              cx={b.cx} cy={b.cy} r={b.r} fill={b.color} fillOpacity="0.88"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.07*i, type: 'spring', stiffness: 130, damping: 14 }}
              style={{ transformOrigin: `${b.cx}px ${b.cy}px`, transition: 'fill-opacity 0.15s' }}
              onMouseEnter={e => {
                e.target.setAttribute('fill-opacity', '1');
                setTip({ x: e.clientX, y: e.clientY, label: b.label, value: b.value });
              }}
              onMouseLeave={e => { e.target.setAttribute('fill-opacity', '0.88'); setTip(null); }}
              onMouseMove={e => tip && setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
            />
            <text x={b.cx} y={b.cy} textAnchor="middle" dominantBaseline="central" fill="#fff"
              style={{ fontSize: Math.max(b.r/2.4, 10), fontWeight: 700, pointerEvents: 'none' }}>
              {b.value}
            </text>
            <text x={b.cx} y={b.cy + b.r + 11} textAnchor="middle" fill="#94A3B8"
              style={{ fontSize: 9.5, fontWeight: 600, pointerEvents: 'none' }}>
              {b.label}
            </text>
          </g>
        ))}
      </svg>
      {tip && <Tooltip x={tip.x} y={tip.y} visible>{tip.label}: {tip.value}</Tooltip>}
    </div>
  );
};

/* ================================================================== */
/*  Mini sparkline (inline trend)                                      */
/* ================================================================== */
export const Sparkline = ({ data = [], color = BLUE, height = 40 }) => {
  const W = 120, H = 40;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const n = data.length;
  const pad = 2;
  const x = i => pad + (i/(n-1)) * (W - 2*pad);
  const y = v => H - pad - ((v-min)/range) * (H - 2*pad);
  const pts = data.map((v,i) => [x(i),y(v)]);
  const line = pts.map((p,i) => `${i?'L':'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <motion.path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2 }} />
    </svg>
  );
};

/* ================================================================== */
/*  Radial progress ring (compact)                                     */
/* ================================================================== */
export const RadialRing = ({ value = 0, max = 100, color = BLUE, size = 60, label }) => {
  const r = (size - 8) / 2;
  const C = 2 * Math.PI * r;
  const len = (value / max) * C;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={6} />
          <motion.circle
            cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
            strokeDasharray={`${C}`} strokeLinecap="round"
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: C - len }}
            transition={{ duration: 1, ease: [0.22,1,0.36,1] }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: '#1E293B' }}>{Math.round((value/max)*100)}%</span>
        </div>
      </div>
      {label && <span style={{ fontSize: 10, color: '#94A3B8', textAlign: 'center', fontWeight: 500 }}>{label}</span>}
    </div>
  );
};

/* ================================================================== */
/*  Section header                                                     */
/* ================================================================== */
export const SectionHead = ({ title, subtitle, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    <span style={{
      width: 32, height: 32, borderRadius: 8,
      background: '#EEF2FF', color: '#4F46E5',
      display: 'grid', placeItems: 'center', flexShrink: 0,
    }}>
      {icon || (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3" />
        </svg>
      )}
    </span>
    <div>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1E293B' }}>{title}</h2>
      {subtitle && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94A3B8' }}>{subtitle}</p>}
    </div>
  </div>
);

/* ================================================================== */
/*  COMPANY ANALYTICS                                                  */
/* ================================================================== */
export const CompanyAnalytics = ({ resumes = [] }) => {
  const stats = useMemo(() => {
    const catMap = new Map();
    const ats = { '0–20':0,'21–40':0,'41–60':0,'61–80':0,'81–100':0 };
    const exp = { '0–1y':0,'1–3y':0,'3–5y':0,'5y+':0 };
    const monthMap = new Map();

    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
      monthMap.set(`${d.getFullYear()}-${d.getMonth()}`, { label: d.toLocaleString('en-US',{month:'short'}), value: 0 });
    }

    resumes.forEach(r => {
      const cat = r.extracted_category || r.category || 'Uncategorised';
      catMap.set(cat, (catMap.get(cat)||0)+1);
      const score = Number(r.ats_score||0);
      if (score<=20) ats['0–20']++;
      else if (score<=40) ats['21–40']++;
      else if (score<=60) ats['41–60']++;
      else if (score<=80) ats['61–80']++;
      else ats['81–100']++;
      const yrs = Number(r.experience_years||0);
      if (yrs<=1) exp['0–1y']++;
      else if (yrs<=3) exp['1–3y']++;
      else if (yrs<=5) exp['3–5y']++;
      else exp['5y+']++;
      if (r.upload_date) {
        const d = new Date(r.upload_date);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (monthMap.has(key)) monthMap.get(key).value++;
      }
    });

    const categories = [...catMap.entries()].sort((a,b) => b[1]-a[1]);
    const top = categories.slice(0,5).map(([label,value],i) => ({ label,value,color:PALETTE[i%PALETTE.length] }));
    const restTotal = categories.slice(5).reduce((s,[,v])=>s+v,0);
    if (restTotal>0) top.push({ label:'Other',value:restTotal,color:'#CBD5E1' });

    return {
      total: resumes.length,
      categoryData: top,
      atsData: Object.entries(ats).map(([label,value])=>({ label,value })),
      expData: Object.entries(exp).map(([label,value])=>({ label,value })),
      trendData: [...monthMap.values()],
    };
  }, [resumes]);

  if (!resumes.length) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <SectionHead title="Library Analytics" subtitle="A quick read on your resume pool" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        <ChartCard title="By Category" subtitle={`${stats.total} total`} compact>
          <Donut data={stats.categoryData} size={120} thickness={16} centerTop={stats.total} centerSub="resumes" />
        </ChartCard>
        <ChartCard title="ATS Score Dist." subtitle="How candidates score" compact>
          <VBars data={stats.atsData.map((d,i)=>({ ...d, color:['#F87171','#FB923C','#FBBF24','#60A5FA','#2563EB'][i] }))} />
        </ChartCard>
        <ChartCard title="Experience Spread" subtitle="Years in the field" compact>
          <VBars data={stats.expData} color="#3B82F6" />
        </ChartCard>
        <ChartCard title="Resumes Added Over Time" subtitle="Uploads — last 6 months" compact style={{ gridColumn: '1/-1' }}>
          <AreaLine data={stats.trendData} color="#2563EB" />
        </ChartCard>
      </div>
    </div>
  );
};

/* ================================================================== */
/*  INDIVIDUAL ANALYTICS                                               */
/* ================================================================== */
export const IndividualAnalytics = ({ applications=[], recommendedJobs=[], stats=[], profileStrength=0 }) => {
  const data = useMemo(() => {
    const statusMap = new Map();
    applications.forEach(a => {
      const key = (a.status||'Applied').trim();
      statusMap.set(key, (statusMap.get(key)||0)+1);
    });
    const statusData = [...statusMap.entries()].map(([label,value],i) => ({
      label, value,
      color: STATUS_COLOR[label.toLowerCase()] || PALETTE[i%PALETTE.length],
    }));

    const now = new Date();
    const weeks = [];
    for (let i=5;i>=0;i--) {
      const end = new Date(now);
      end.setDate(now.getDate()-i*7);
      weeks.push({ label:i===0?'This wk':`${i}w`, start:new Date(end).setDate(end.getDate()-6), end:end.getTime(), value:0 });
    }
    applications.forEach(a => {
      const t = a.applied_date ? new Date(a.applied_date).getTime() : 0;
      const bucket = weeks.find(w => t>=w.start && t<=w.end);
      if (bucket) bucket.value++;
    });
    const trendData = weeks.map(w => ({ label:w.label, value:w.value }));

    const matchData = recommendedJobs.slice(0,6).map(j => ({
      label: (j.company||j.title||'Job').slice(0,6),
      value: Number(j.match_score||0),
    }));

    return { statusData, trendData, matchData, totalApps: applications.length };
  }, [applications, recommendedJobs]);

  return (
    <div style={{ marginBottom: 16 }}>
      <SectionHead title="Your Activity" subtitle="Insights from your job search" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <ChartCard title="Applications by Status" subtitle={`${data.totalApps} tracked`} compact>
          <Donut data={data.statusData} size={120} thickness={16} centerTop={data.totalApps} centerSub="sent" />
        </ChartCard>
        <ChartCard title="Applications Over Time" subtitle="Last 6 weeks" compact>
          <AreaLine data={data.trendData} color="#3B82F6" />
        </ChartCard>
        <ChartCard title="Top Match Scores" subtitle="Recommended roles" compact>
          <VBars data={data.matchData} color="#2563EB" suffix="%" />
        </ChartCard>
        <ChartCard title="Profile Strength" subtitle="Complete for better matches" compact>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
            <Donut
              data={[
                { label:'Complete', value:profileStrength, color:BLUE },
                { label:'Remaining', value:Math.max(100-profileStrength,0), color:TRACK },
              ]}
              size={110} thickness={14}
              centerTop={`${profileStrength}%`} centerSub="done"
              showLegend={false}
            />
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default { IndividualAnalytics, CompanyAnalytics, Donut, VBars, AreaLine, PieChart, HBars, StackedBar, Bubbles, Sparkline, RadialRing };